import * as React from 'react';
import { AlertTriangle, Info, Pencil } from 'lucide-react';
import type { StatusChipTone } from '@/components/ui/status-chip';
import { Callout, WorkspaceBanner, WorkspaceButton } from '@/components/ui/workspace';
import { cn } from '@/lib/utils/cn';
import type { MapMetricsDto, MapValidationState } from '@/lib/api/process-maps';

/**
 * Small pieces of the map screens (T-0189), shared by engagement maps and
 * the framework's reference processes. Prototype: `mapsView()`,
 * `refProcessView()` and `.map-*` / `.bp-*` in `docs/discovery/prototype/`.
 * Everything here sits above, below or beside the canvas, never over it
 * (OQ-V).
 */

/** A version's validation state, in the prototype's words. */
export function validationChip(
  state: MapValidationState,
  validatedBefore: boolean,
): { tone: StatusChipTone; label: string } {
  switch (state) {
    case 'validated':
      return { tone: 'healthy', label: 'Validated' };
    case 'requested':
      return { tone: 'amber', label: 'Awaiting validation' };
    case 'changes_requested':
      return { tone: 'at-risk', label: 'Client comments' };
    default:
      return validatedBefore
        ? { tone: 'at-risk', label: 'Changed since validation' }
        : { tone: 'neutral', label: 'Not validated yet' };
  }
}

/** One row under the toolbar: a chip and a line of plain words. */
export function MapProvenance({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2 px-4 pt-2 text-meta text-ink-500">
      {children}
    </div>
  );
}

export function Swatch({ className }: { className: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn('inline-block h-3 w-5 flex-none rounded-[3px] border-2', className)}
    />
  );
}

/**
 * What the marks on the map mean. Under OQ-V the prototype's tags are drawn
 * as colour and outline on the shapes, so the legend shows those.
 */
export function MapLegend({
  kind,
  previewing,
  hint,
}: {
  kind: 'engagement' | 'reference';
  previewing: boolean;
  hint: string;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 pt-2.5 text-meta text-ink-500">
      {kind === 'engagement' ? (
        <>
          <span className="inline-flex items-center gap-1.5">
            <Swatch className="border-posture-critical bg-paper-0" />
            pain point
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Swatch className="border-ink-700 bg-paper-0" />
            cites confirmed evidence
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Swatch className="border-dashed border-ink-700 bg-paper-50" />
            unconfirmed: nobody has described it yet
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Swatch className="border-accent-amber-600 bg-accent-amber-50" />
            needs attention
          </span>
        </>
      ) : null}
      {previewing ? (
        <>
          <span className="inline-flex items-center gap-1.5">
            <Swatch className="border-dashed border-accent-amber-600 bg-accent-amber-50" />
            proposed change
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Swatch className="border-posture-healthy bg-posture-healthy-tint" />
            proposed new step
          </span>
        </>
      ) : null}
      <span className="min-w-0 flex-1 text-right max-[900px]:text-left">{hint}</span>
    </div>
  );
}

/** The counts under a map, all counted by code on the main path (rule 22). */
export function MapMetricsRow({
  metrics,
  kind,
  proposed = false,
}: {
  metrics: MapMetricsDto;
  kind: 'engagement' | 'reference';
  /** The counts are of the map with the change shown on it, not yet applied. */
  proposed?: boolean;
}) {
  // [count, label for one, label for any other count]
  const items: Array<[number, string, string]> =
    kind === 'engagement'
      ? [
          [metrics.steps_people_do, 'step people do', 'steps people do'],
          [metrics.steps_on_paper, 'on paper or in a spreadsheet', 'on paper or in a spreadsheet'],
          [metrics.handoffs, 'hand-off between people', 'hand-offs between people'],
          [metrics.pain_points, 'pain point on the map', 'pain points on the map'],
          [metrics.unconfirmed_steps, 'step unconfirmed', 'steps unconfirmed'],
        ]
      : [
          [metrics.steps_people_do, 'step people do', 'steps people do'],
          [metrics.steps_on_paper, 'on paper or in a spreadsheet', 'on paper or in a spreadsheet'],
          [metrics.steps_system_does, 'step the system does', 'steps the system does'],
          [metrics.handoffs, 'hand-off between people', 'hand-offs between people'],
        ];
  return (
    <div
      data-testid="map-metrics"
      className="flex flex-wrap items-baseline gap-x-5 gap-y-1 text-body-sm text-ink-700"
    >
      {items.map(([n, one, many]) => (
        <span key={many}>
          <b className="font-semibold tabular-nums text-ink-900">{n}</b> {n === 1 ? one : many}
        </span>
      ))}
      <span className="text-meta text-ink-500">
        {proposed
          ? 'Counted by code on the main path, with the change shown.'
          : 'Counted by code on the main path.'}
      </span>
    </div>
  );
}

export interface MapStatus {
  text: string;
  tone: 'default' | 'critical';
}

/**
 * Messages about the map (saved, refused, sent) go here, in the map column
 * above the canvas, instead of a toast that could cover it (OQ-V).
 */
export function MapStatusLine({
  status,
  onDismiss,
}: {
  status: MapStatus | null;
  onDismiss: () => void;
}) {
  return (
    <div role="status" aria-live="polite" className="px-4 empty:hidden">
      {status ? (
        <Callout
          tone={status.tone === 'critical' ? 'warn' : 'tip'}
          icon={status.tone === 'critical' ? AlertTriangle : Info}
          className="mt-2"
          actions={
            <button
              type="button"
              onClick={onDismiss}
              className="font-medium underline underline-offset-[3px]"
            >
              Dismiss
            </button>
          }
        >
          {status.text}
        </Callout>
      ) : null}
    </div>
  );
}

/** Edit mode's banner (prototype `editBanner`). */
export function EditBanner({
  reference,
  note,
  onDiscard,
}: {
  reference: boolean;
  /** What edits mean for this kind of map, in place of the engagement map's sentence. */
  note?: string;
  onDiscard: () => void;
}) {
  return (
    <div className="px-4 pt-2">
      <WorkspaceBanner
        actions={
          <WorkspaceButton variant="tertiary" onClick={onDiscard}>
            Discard edits
          </WorkspaceButton>
        }
      >
        <span className="inline-flex items-start gap-2">
          <Pencil className="mt-0.5 h-4 w-4 flex-none text-accent-amber-700" aria-hidden="true" />
          <span>
            <strong className="font-semibold text-ink-900">Editing.</strong> Drag a step into
            another lane or column and Caliber lays the map out again; positions are not kept. Add
            steps or decisions from the palette, connect them, and double-click to rename.{' '}
            {reference
              ? 'Done editing checks your edits as one change for the framework draft.'
              : (note ??
                'Steps you keep keep their citations; a step you add has none until you link evidence. Done editing checks your edits as one change for you to apply.')}
          </span>
        </span>
      </WorkspaceBanner>
    </div>
  );
}

/**
 * A change checked by code and shown on the map, waiting for Apply or
 * Discard (Decision 51: nothing changes without Apply).
 */
export function PendingBanner({
  title,
  lines,
  busy,
  applyDisabledReason,
  onApply,
  onDiscard,
}: {
  title: string;
  lines: string[];
  busy: boolean;
  applyDisabledReason?: string | null;
  onApply: () => void;
  onDiscard: () => void;
}) {
  return (
    <div className="px-4 pt-2">
      <WorkspaceBanner
        actions={
          <>
            <WorkspaceButton variant="tertiary" disabled={busy} onClick={onDiscard}>
              Discard
            </WorkspaceButton>
            <WorkspaceButton
              variant="primary"
              disabled={busy || Boolean(applyDisabledReason)}
              title={applyDisabledReason ?? undefined}
              onClick={onApply}
            >
              Apply
            </WorkspaceButton>
          </>
        }
      >
        <strong className="font-semibold text-ink-900">{title}</strong> {lines.join(' ')}{' '}
        <span className="text-ink-500">Shown on the map. Nothing changes until you apply it.</span>
      </WorkspaceBanner>
    </div>
  );
}
